import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import * as mockDb from '../mocks/db';
import type {
  ActivityFeed,
  AppProfile,
  CreateLinkInput,
  Link,
  LinkAnalytics,
  ListLinksParams,
  ListLinksResult,
} from '../types/link';

// Inside the xNotify Customer Portal we ALWAYS want the real backend —
// the App's session has a real x-api-key, the Smart Links API is running, and
// the user-visible page must never show mock data. Setting REACT_APP_USE_MOCKS
// to "true" is the explicit opt-in for offline portal-dev mode only.
export const USE_MOCKS =
  (process.env.REACT_APP_USE_MOCKS ?? 'false').toLowerCase() === 'true';

// Fallback port is 4001 — matches the Smart Links API's actual local
// port. Do NOT lower this to 4000; that's xNotify Push Hub's port and
// hitting it sends the App into a CORS-rejected preflight loop.
const API_BASE = process.env.REACT_APP_SMARTLINKS_API_URL || 'http://localhost:4001';

export interface ApiError {
  status: number;
  message: string;
}

/** Thrown by the mock transport; carries an HTTP-like status. */
export class ApiErrorException extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiErrorException';
    this.status = status;
  }
}

/** Operations the UI performs, abstracted so mock + real share one type. */
export type ApiArg =
  | { op: 'listLinks'; params: ListLinksParams }
  | { op: 'getLink'; id: string }
  | { op: 'createLink'; input: CreateLinkInput }
  | { op: 'expireLink'; id: string }
  | { op: 'getAnalytics'; id: string; windowDays: number }
  | { op: 'getActivity'; limit?: number; days?: number }
  | { op: 'getAppProfile' }
  | { op: 'saveAppProfile'; profile: AppProfile };

type ApiResult = ListLinksResult | Link | LinkAnalytics | ActivityFeed | { profile: AppProfile };

// ---- mock transport -------------------------------------------------------

async function mockTransport(arg: ApiArg): Promise<ApiResult> {
  // Small artificial latency so loading states are exercised in dev.
  await new Promise((r) => setTimeout(r, 180));
  switch (arg.op) {
    case 'listLinks':
      return mockDb.listLinks(arg.params);
    case 'getLink': {
      const l = mockDb.getLink(arg.id);
      if (!l) throw new ApiErrorException(404, 'Link not found');
      return l;
    }
    case 'createLink':
      return mockDb.createLink(arg.input);
    case 'expireLink': {
      const l = mockDb.expireLink(arg.id);
      if (!l) throw new ApiErrorException(404, 'Link not found');
      return l;
    }
    case 'getAnalytics': {
      const a = mockDb.getAnalytics(arg.id, arg.windowDays);
      if (!a) throw new ApiErrorException(404, 'Link not found');
      return a;
    }
    case 'getActivity':
      return { events: [], range: { limit: arg.limit ?? 200 } };
    default:
      throw new ApiErrorException(400, 'Unknown operation');
  }
}

// ---- real transport (axios → Smart Links API) --------------------------------
// RULE-01: customer_id is never sent; the backend resolves it from the token.
// SL_010: the portal authenticates with the xNotify user access token
// (x-access-token) ONLY. The management routes are token-only; the raw tenant
// x-api-key is no longer sent from the browser (it stays the credential for the
// mobile ingest SDK on /v1/attribution, not the portal). Never hard-coded.

const http = axios.create({ baseURL: API_BASE, timeout: 10_000 });
http.interceptors.request.use((config) => {
  // Auth: the xNotify user access token (JWT) — the SAME hardened token the rest
  // of the App sends. The Smart Links API verifies it (via RRS); its CORS
  // Allow-Headers includes x-access-token so the preflight passes.
  if (typeof window !== 'undefined') {
    const accessToken = window.localStorage.getItem('customer-portal') || '';
    if (accessToken) config.headers['x-access-token'] = accessToken;
  }
  return config;
});

function realRequest(arg: ApiArg): AxiosRequestConfig {
  switch (arg.op) {
    case 'listLinks':
      return { method: 'GET', url: '/links', params: arg.params };
    case 'getLink':
      return { method: 'GET', url: `/links/${arg.id}` };
    case 'createLink':
      return { method: 'POST', url: '/links', data: arg.input };
    case 'expireLink':
      return { method: 'PATCH', url: `/links/${arg.id}`, data: { isActive: false } };
    case 'getAnalytics':
      return { method: 'GET', url: `/links/${arg.id}/analytics`, params: { windowDays: arg.windowDays } };
    case 'getActivity':
      return { method: 'GET', url: '/links/activity', params: { limit: arg.limit, days: arg.days } };
    case 'getAppProfile':
      return { method: 'GET', url: '/app-profile' };
    case 'saveAppProfile':
      return { method: 'PUT', url: '/app-profile', data: arg.profile };
    default:
      return { method: 'GET', url: '/' };
  }
}

// Object map {label: count} → sorted BreakdownSlice[] (desc, drop zeros).
function toSlices(obj: Record<string, unknown> | undefined | null) {
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj)
    .map(([label, v]) => ({ label, value: Number(v) || 0 }))
    .filter((s) => s.value > 0)
    .sort((a, b) => b.value - a.value);
}

// Event Redis Only backend (FINAL_PROPOSED_ARCHITECTURE.md) returns
// { realtime, byCountry, historical, lastUpdated }. The UI renders LinkAnalytics
// ({ totals, timeseries, byPlatform, byChannel, byCountry, recentEvents }). Adapt
// the new shape into LinkAnalytics. If the backend still returns the OLD shape
// (pre-deploy), pass it through unchanged — so the portal works during rollout.
function adaptAnalytics(raw: any, windowDays: number): LinkAnalytics {
  if (!raw || typeof raw !== 'object') {
    return { linkId: '', shortCode: '', windowDays, totals: { clicks: 0, installs: 0, opens: 0, installRate: 0, ctr: 0 }, timeseries: [], byPlatform: [], byChannel: [], recentEvents: [] };
  }
  if (raw.totals && Array.isArray(raw.timeseries)) return raw as LinkAnalytics; // legacy shape
  const rt = raw.realtime || {};
  const historical = Array.isArray(raw.historical) ? raw.historical : [];
  return {
    linkId: raw.linkId ?? '',
    shortCode: raw.shortCode ?? '',
    windowDays: raw.windowDays ?? windowDays,
    totals: {
      clicks: Number(rt.clicks) || 0,
      installs: Number(rt.installs) || 0,
      opens: Number(rt.opens) || 0,
      uniqueDevices: Number(rt.uniqueDevices) || 0,
      installRate: Number(rt.installRate) || 0,
      ctr: 0, // not exposed by the Event-Redis metrics hash
    },
    // ClickHouse trend returns { date, clicks, unique_users }; installs/opens are
    // not in the trend query → 0 per day (real-time totals carry the live counts).
    timeseries: historical.map((h: any) => ({
      date: String(h.date),
      clicks: Number(h.clicks) || 0,
      installs: 0,
      opens: 0,
    })),
    byPlatform: toSlices(rt.byPlatform),
    byChannel: toSlices(rt.byChannel),
    byCountry: toSlices(raw.byCountry),
    // byCity/byRegion/byDeviceModel/byOS/byOSVersion + recentEvents are not part
    // of the Event-Redis read model → empty (UI shows each section's empty state).
    recentEvents: [],
  };
}

async function realTransport(arg: ApiArg): Promise<ApiResult> {
  const res = await http.request<ApiResult>(realRequest(arg));
  if (arg.op === 'getAnalytics') return adaptAnalytics(res.data, arg.windowDays);
  return res.data;
}

/** RTK Query base query that routes to the mock or the real backend. */
export const appBaseQuery: BaseQueryFn<ApiArg, unknown, ApiError> = async (arg) => {
  try {
    const data = USE_MOCKS ? await mockTransport(arg) : await realTransport(arg);
    return { data };
  } catch (err) {
    if ((err as AxiosError).isAxiosError) {
      const ax = err as AxiosError<{
        message?: string;
        error?: string;
        detail?: string;
        issues?: Array<{ path: string; message: string }>;
      }>;
      const body = ax.response?.data;
      // Smart Links API error shapes (most → least specific):
      //   { error: 'validation_failed', issues: [{ path, message }] }
      //   { error: 'invalid_purpose' | 'no_destination_resolved' | ... , detail?: string }
      //   { message: string }   ← legacy / generic
      // Fall back to axios's "Request failed with status code N" only as a last resort.
      const issueSummary = body?.issues?.length
        ? body.issues.map((i) => `${i.path}: ${i.message}`).join('; ')
        : undefined;
      const message =
        issueSummary ??
        body?.message ??
        body?.detail ??
        body?.error ??
        ax.message;
      return { error: { status: ax.response?.status ?? 0, message } };
    }
    if (err instanceof ApiErrorException) {
      return { error: { status: err.status, message: err.message } };
    }
    const apiErr = err as ApiError;
    return { error: { status: apiErr.status ?? 500, message: apiErr.message ?? 'Request failed' } };
  }
};
